// ---------------------------------------------------------------------------
// A INTENÇÃO de iniciar o Foco — o start otimista que sobrevive ao processo.
//
// ## Por que existe (BUG-275, 17/09)
//
// O Foco arranca OTIMISTA (`PENDING_SESSION_ID`) e a sessão real chega em
// background — a callable leva 5-6 s em instância quente. O gesto mais comum
// do produto cabe inteiro nessa janela: iniciar e bloquear a tela. E no app
// das lojas voltar do bloqueio na Home ou no Perfil RECRIA a WebView (a cura
// preventiva do BUG-197): é um boot de JS, e o pedido que estava em voo morre
// com ela. Se ele não tinha chegado ao servidor, não existe sessão para a
// recuperação achar — o Foco simplesmente some, sem uma linha na tela, e só a
// segunda tentativa "conta". Na web o transporte cai no resume e o `catch`
// pinta "Não foi possível iniciar" para uma sessão que o servidor pode ter
// criado.
//
// Antes desta fatia a intenção de iniciar vivia SÓ na memória do processo.
// Aqui ela é persistida ANTES da rede e repetida depois — no mesmo processo
// (o `catch` do start) ou no boot seguinte (a recuperação do provider), com o
// instante ORIGINAL (`localStartMs`), que o servidor honra com teto
// (`resumeFromMs` → `MAX_FOCUS_START_BACKDATE_SEC`).
//
// ## O que NÃO pode cair
//
//  - A intenção morre com toda decisão LOCAL que a torna obsoleta: o ack do
//    start (a sessão existe), `resetLocal` (cancelar, "Tentar novamente"),
//    `closeFocus`, uma recusa DEFINITIVA do servidor. Sem isso, o boot
//    repetiria uma sessão que a pessoa cancelou.
//  - Recusa definitiva ≠ transporte. `invalid-argument` ou `failed-precondition`
//    não vão melhorar na repetição; `unavailable`, `deadline-exceeded`,
//    `internal` e o `TypeError` de rede são exatamente o caso do replay.
//    A lista é de DEFINITIVOS: código desconhecido cai no lado que repete,
//    porque repetir um start é idempotente no servidor (`reuse` dentro da
//    folga) e não repetir perde a sessão.
//  - A janela é a MESMA da coleta (`MAX_COLLECT_WAIT_SEC`): acima dela o
//    crédito cairia no `dayKey` de hoje e mentiria na trilha — a mesma régua
//    que descarta a sessão vencida velha.
//  - Falhar no storage é "não sei" = comportamento de antes (sem replay).
//    Mesma doutrina do `focusPrefs` e do `focusPresentation`.
// ---------------------------------------------------------------------------

import { MAX_COLLECT_WAIT_SEC, START_ACK_GRACE_MS } from "./focusElapsed";

const STORAGE_KEY = "livra_focus_start_intent_v1";

/** O que basta para repetir o `startFocusSession` — e nada de identidade de sessão. */
export interface FocusStartIntent {
  bookId: string;
  /** 0 = livre. Sessão por páginas manda 0 aqui e o alvo em `targetPages`/`targetPercent`. */
  durationSec: number;
  targetPages?: number;
  targetPercent?: number;
  /** O instante em que a pessoa INICIOU — o relógio do start otimista. */
  localStartMs: number;
  /** A foto do livro que viaja no start (BUG-215). Rótulo, nunca identidade. */
  snapshot?: {
    title?: string | null;
    coverUrl?: string | null;
    format?: "physical" | "kindle" | null;
  } | null;
}

export type StartIntentReplayRoute = "replay" | "stale";

/**
 * Esta intenção ainda vale um replay? A régua é a da coleta: uma sessão que
 * teria vencido há mais de `MAX_COLLECT_WAIT_SEC` também não seria coletada.
 * Intenção com instante no futuro (relógio mexido) é `stale` — nada a repetir.
 */
export function resolveStartIntentReplay(
  intent: Pick<FocusStartIntent, "localStartMs">,
  nowMs: number,
): StartIntentReplayRoute {
  const ageMs = nowMs - intent.localStartMs;
  if (!Number.isFinite(ageMs) || ageMs < 0) return "stale";
  return ageMs <= MAX_COLLECT_WAIT_SEC * 1000 ? "replay" : "stale";
}

/**
 * A sessão encontrada no servidor É a desta intenção? A mesma folga do ack do
 * start (`resolveStartAckRoute`): começou no instante da intenção ou depois
 * (o servidor pode ter recuado até `localStartMs`, nunca antes dele).
 */
export function intentMatchesSession(
  intent: Pick<FocusStartIntent, "localStartMs">,
  sessionStartedAtMs: number | null | undefined,
): boolean {
  if (typeof sessionStartedAtMs !== "number" || !Number.isFinite(sessionStartedAtMs)) return false;
  return sessionStartedAtMs >= intent.localStartMs - START_ACK_GRACE_MS;
}

/**
 * Códigos de `functions/*` que NÃO melhoram repetindo. Tudo o mais — inclusive
 * o desconhecido e a rejeição sem código (rede) — é transporte, e repete.
 */
const DEFINITIVE_START_CODES = new Set([
  "functions/invalid-argument",
  "functions/failed-precondition",
  "functions/unauthenticated",
  "functions/permission-denied",
  "functions/not-found",
  "functions/already-exists",
  "functions/out-of-range",
  "functions/unimplemented",
  "functions/resource-exhausted",
]);

export function isDefinitiveStartRejection(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const code = (err as { code?: unknown }).code;
  return typeof code === "string" && DEFINITIVE_START_CODES.has(code);
}

function isUsableIntent(v: unknown): v is FocusStartIntent {
  if (!v || typeof v !== "object") return false;
  const rec = v as Partial<FocusStartIntent>;
  if (typeof rec.bookId !== "string" || rec.bookId.trim().length === 0) return false;
  if (typeof rec.localStartMs !== "number" || !Number.isFinite(rec.localStartMs)) return false;
  if (typeof rec.durationSec !== "number" || !Number.isFinite(rec.durationSec) || rec.durationSec < 0) {
    return false;
  }
  if (rec.targetPages != null && typeof rec.targetPages !== "number") return false;
  if (rec.targetPercent != null && typeof rec.targetPercent !== "number") return false;
  return true;
}

/** Grava a intenção vigente — ANTES da rede, é o ponto. */
export function rememberFocusStartIntent(intent: FocusStartIntent): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
  } catch {
    // Sem storage a intenção só não sobrevive ao processo — o app segue igual.
  }
}

/** A intenção guardada, ou `null` quando não há (ou não dá para ler). */
export function readFocusStartIntent(): FocusStartIntent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isUsableIntent(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Esquece a intenção — a sessão existe, ou deixou de fazer sentido. */
export function forgetFocusStartIntent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // idem
  }
}
